import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { licenseApi } from '../services/licenseApi';
import { useAuth } from './AuthContext';

const LicenseContext = createContext(null);

export const LicenseProvider = ({ children }) => {
  const { authenticated } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(() => {
    if (!authenticated) return;
    setLoading(true);
    licenseApi.getStatus()
      .then(res => setStatus(res.data))
      .catch(() => setStatus(null))
      .finally(() => setLoading(false));
  }, [authenticated]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const isModuleLicensed = useCallback((moduleName) => {
    if (!status) return true;
    if (status.enforcement !== 'strict') return true;
    if (!status.loaded || !status.valid) return false;
    return (status.modules || []).includes(moduleName);
  }, [status]);

  const isAppBlocked = useMemo(() => {
    if (!status) return false;
    if (status.enforcement !== 'strict') return false;
    if (!status.loaded) return true;
    return !status.valid;
  }, [status]);

  const showWarning = useMemo(() => {
    if (!status) return false;
    if (status.enforcement === 'strict') return false;
    return !status.loaded || !status.valid;
  }, [status]);

  const value = useMemo(() => ({
    status,
    loading,
    isModuleLicensed,
    isAppBlocked,
    showWarning,
    refresh: fetchStatus,
  }), [status, loading, isModuleLicensed, isAppBlocked, showWarning, fetchStatus]);

  return <LicenseContext.Provider value={value}>{children}</LicenseContext.Provider>;
};

export const useLicense = () => {
  const ctx = useContext(LicenseContext);
  if (!ctx) throw new Error('useLicense doit être utilisé dans <LicenseProvider>');
  return ctx;
};

export default LicenseContext;
